import { quoteVersion } from "@platform/domain";
import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { CurrencyCode, Money } from "../common/money.js";
import { Quantity } from "../common/quantity.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { OrderView } from "./order.view.js";
import { HsCode, Incoterm, TradeAddress, TradeParties, TradeSide } from "./terms.js";
/** Quote versions, counters, and acceptance into an order (INQ-2, INQ-3, ORD-1, PAY-8, card 053). */
export const QuoteStatus = z.enum(quoteVersion.states).meta({ id: "QuoteStatus" });
/**
 * The two phase-1 schedules (card 049): the full amount when the order is created, or a deposit then and the balance
 * when the goods are ready to ship or shipped.
 */
export const ScheduleTemplate = z
    .discriminatedUnion("kind", [
    z.object({ kind: z.literal("full") }),
    z.object({
        kind: z.literal("deposit_balance"),
        depositBps: z
            .number()
            .int()
            .min(1)
            .max(9_999)
            .describe("The deposit's share in basis points: 3000 is 30%"),
        balanceDue: z.enum(["ready_to_ship", "shipped"]),
        depositRelease: z
            .enum(["receipt_confirmed", "shipment_recorded"])
            .default("receipt_confirmed")
            .describe("The deposit is released on shipment evidence only when the quote says so"),
    }),
])
    .meta({ id: "ScheduleTemplate" });
export const ScheduleItemType = z
    .enum(["deposit", "balance", "full", "top_up"])
    .meta({ id: "ScheduleItemType" });
export const DueTrigger = z
    .enum(["order_created", "ready_to_ship", "shipped", "change_accepted"])
    .meta({ id: "DueTrigger" });
export const ReleaseTrigger = z
    .enum(["receipt_confirmed", "shipment_recorded"])
    .meta({ id: "ReleaseTrigger" });
/** A schedule item as a quote plans it; the order's items carry status and dates as well. */
export const PlannedScheduleItem = z
    .object({
    seq: z.number().int().positive(),
    type: ScheduleItemType,
    amount: Money,
    dueTrigger: DueTrigger,
    releaseTrigger: ReleaseTrigger,
})
    .meta({ id: "PlannedScheduleItem" });
export const QuoteCharge = z
    .object({ kind: z.enum(["freight", "insurance", "other"]), amount: Money })
    .meta({ id: "QuoteCharge", description: "Freight, insurance, or another charge, in the quote's currency" });
export const QuoteLine = z
    .object({
    id: Id,
    productId: Id,
    skuId: Id.nullable(),
    productName: LocalizedText,
    skuCode: z.string().nullable(),
    hsCode: z.string().nullable(),
    quantity: Quantity,
    unit: z.string(),
    unitPrice: Money,
    lineTotal: Money.describe("Quantity times unit price, rounded half-up to the minor unit"),
    sample: z.boolean().describe("One sample of the product (card 096)"),
})
    .meta({ id: "QuoteLine" });
/** A rate between two currencies with where it came from (ADR 0019). */
export const FxRateView = z
    .object({
    base: CurrencyCode,
    quote: CurrencyCode,
    rate: z.string().describe("One unit of `base` in `quote`, as a decimal string"),
    source: z.string().describe("Such as `ecb:2026-09-28`"),
    capturedAt: Timestamp,
})
    .meta({ id: "FxRate" });
/** What each side sees before accepting (PAY-8). */
export const QuoteAmounts = z
    .object({
    total: Money.describe("What the buyer pays, in the quote's currency, which is the settlement currency"),
    listingCurrency: CurrencyCode.describe("The currency the products are listed in"),
    totalInListingCurrency: Money.nullable().describe("The total converted at `rate`; null when the currencies are the same or no fresh rate exists"),
    rate: FxRateView.nullable().describe("Indicative reference rate until the quote is accepted; then the order's snapshot"),
    platformFee: Money.nullable().describe("The seller's side only: the platform fee on the total under the RuleSet in force (ADR 0007)"),
    sellerPayout: Money.nullable().describe("The seller's side only: the total less the fee"),
    rulesetVersion: z.number().int().positive(),
    paymentMethods: z
        .array(z.enum(["airwallex", "bank_transfer"]))
        .describe("How the buyer could pay now; acceptance is refused with none"),
})
    .meta({ id: "QuoteAmounts" });
export const QuoteCounter = z
    .object({
    message: z.string(),
    lines: z.array(z.object({ quoteLineId: Id, quantity: Quantity.optional(), unitPrice: Money.optional() })),
    requestedAt: Timestamp,
})
    .meta({ id: "QuoteCounter" });
export const QuoteView = z
    .object({
    id: Id,
    inquiryId: Id,
    versionNo: z.number().int().positive(),
    status: QuoteStatus,
    side: TradeSide.describe("The caller's side"),
    ...TradeParties,
    currency: CurrencyCode,
    lines: z.array(QuoteLine),
    charges: z.array(QuoteCharge),
    subtotal: Money,
    total: Money,
    incoterm: Incoterm,
    namedPlace: z.string(),
    portOfLoading: z.string().nullable(),
    shippingOrigin: TradeAddress,
    leadTimeDays: z.number().int().nonnegative(),
    validUntil: Timestamp,
    scheduleTemplate: ScheduleTemplate,
    schedule: z.array(PlannedScheduleItem).describe("The items the order would have; they sum to the total"),
    notes: z.string().nullable(),
    counter: QuoteCounter.nullable().describe("The buyer's requested changes, when countered"),
    orderId: Id.nullable().describe("The order, once accepted"),
    amounts: QuoteAmounts,
    conversationId: Id.nullable(),
    inquiryVersion: z.number().int().positive().describe("Sent as `If-Match` to accept"),
    createdAt: Timestamp,
    actions: z.array(z.enum(["counter", "accept"])).describe("What the caller may do now"),
})
    .meta({ id: "QuoteView" });
export const QuoteSummary = z
    .object({
    id: Id,
    versionNo: z.number().int().positive(),
    status: QuoteStatus,
    total: Money,
    validUntil: Timestamp,
    createdAt: Timestamp,
})
    .meta({ id: "QuoteSummary" });
/** The quote as its card shows it in the deal conversation, read afresh for each viewer. */
export const QuoteCardData = z
    .object({
    versionNo: z.number().int().positive(),
    status: QuoteStatus,
    ...TradeParties,
    lines: z.array(QuoteLine.pick({
        productName: true,
        skuCode: true,
        quantity: true,
        unit: true,
        unitPrice: true,
        lineTotal: true,
    })),
    total: Money,
    incoterm: Incoterm,
    namedPlace: z.string(),
    leadTimeDays: z.number().int().nonnegative(),
    validUntil: Timestamp,
    schedule: z.array(PlannedScheduleItem.pick({ type: true, amount: true, dueTrigger: true })),
})
    .meta({ id: "QuoteCardData" });
const Party = { surface: "tenant", auth: "user" };
const QuoteId = z.object({ quoteId: Id });
export const createQuote = defineRoute({
    ...Party,
    permission: "quote:create",
    id: "trade.quotes.create",
    method: "POST",
    path: "/v1/inquiries/{inquiryId}/quotes",
    marks: ["tenant", "verified", "idem"],
    summary: "The shop issues the next quote version: lines from the inquiry with unit prices in one currency, charges, Incoterm and named place, port of loading, shipping origin from its address book, lead time, validity, payment schedule, and notes. Totals are computed here; the version never changes after, and it supersedes the previous one",
    prd: ["INQ-2"],
    request: {
        params: z.object({ inquiryId: Id }),
        body: z.object({
            currency: CurrencyCode,
            lines: z
                .array(z.object({
                inquiryLineId: Id,
                quantity: Quantity.optional().describe("The inquiry line's quantity when absent"),
                unitPrice: Money.describe("In the quote's currency"),
                hsCode: HsCode.optional(),
            }))
                .min(1)
                .max(50),
            charges: z.array(QuoteCharge).max(3).default([]),
            incoterm: Incoterm,
            namedPlace: z.string().trim().min(1).max(200),
            portOfLoading: z.string().trim().min(1).max(200).optional(),
            shippingOriginAddressId: Id.describe("An address in the seller's address book"),
            leadTimeDays: z.number().int().min(0).max(730),
            validityDays: z
                .number()
                .int()
                .min(1)
                .max(365)
                .optional()
                .describe("The RuleSet's default when absent, and at most its maximum"),
            paymentSchedule: ScheduleTemplate,
            notes: z.string().trim().min(1).max(2000).optional(),
        }),
    },
    responses: { 201: { description: "The quote version", schema: QuoteView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "ENTERPRISE_NOT_VERIFIED",
        "ENTERPRISE_SUSPENDED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "CURRENCY_NOT_ENABLED",
        "FX_UNAVAILABLE",
        "COUNTRY_NOT_SERVED",
    ],
});
export const getQuote = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.quotes.get",
    method: "GET",
    path: "/v1/quotes/{quoteId}",
    marks: ["tenant"],
    summary: "One quote version with the amounts each side sees before accepting: the buyer's total in both currencies, and for the seller the fee and expected payout",
    prd: ["INQ-2", "PAY-8"],
    request: { params: QuoteId },
    responses: { 200: { description: "The quote version", schema: QuoteView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const counterQuote = defineRoute({
    ...Party,
    permission: "quote:accept",
    id: "trade.quotes.counter",
    method: "POST",
    path: "/v1/quotes/{quoteId}/counter",
    marks: ["tenant", "idem"],
    summary: "The buyer asks for changes to the latest valid version, which asks the shop for the next one",
    prd: ["INQ-3"],
    request: {
        params: QuoteId,
        body: z.object({
            message: z.string().trim().min(1).max(2000),
            lines: z
                .array(z.object({ quoteLineId: Id, quantity: Quantity.optional(), unitPrice: Money.optional() }))
                .max(50)
                .default([]),
        }),
    },
    responses: { 200: { description: "The countered version", schema: QuoteView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "QUOTE_EXPIRED",
        "QUOTE_SUPERSEDED",
    ],
});
export const acceptQuote = defineRoute({
    ...Party,
    permission: "quote:accept",
    id: "trade.quotes.accept",
    method: "POST",
    path: "/v1/quotes/{quoteId}/accept",
    marks: ["tenant", "verified", "idem", "if-match"],
    summary: "The buyer accepts this version with a delivery address from its address book (`If-Match`: the inquiry's version). The order is created in the same transaction, with its totals, lines, schedule, the FX snapshot when the currencies differ, and the addresses as they are now; its first payment is due at once. A seller that may not trade now, being suspended, closed, or behind on an agreement, takes no order (`SHOP_NOT_SELLING`)",
    prd: ["INQ-3", "ORD-1", "PAY-8"],
    request: { params: QuoteId, body: z.object({ deliveryAddressId: Id }) },
    responses: { 201: { description: "The order", schema: OrderView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "ENTERPRISE_NOT_VERIFIED",
        "ENTERPRISE_SUSPENDED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "QUOTE_EXPIRED",
        "QUOTE_SUPERSEDED",
        "SHOP_NOT_SELLING",
        "COUNTRY_NOT_SERVED",
        "VERSION_CONFLICT",
        "CURRENCY_NOT_ENABLED",
        "PAYMENT_METHOD_DISABLED",
        "FX_UNAVAILABLE",
    ],
});
export const quoteRoutes = [createQuote, getQuote, counterQuote, acceptQuote];
//# sourceMappingURL=quote.routes.js.map